'use client';
import React from 'react';
import { RichTextParagraph } from './subcomponents/paragraph';
import { RichTextList } from './subcomponents/list';
import { RichTextOrderList } from './subcomponents/ordered-list';
import { RichTextHeading, TYPE_HEADING } from './subcomponents/heading';
import { RichTextPreformatted } from './subcomponents/preformatted';
import { classes } from '@/utils/classes/classes';
import type { RichTextAccumulator, RichTextElement, RichTextProps } from '@/app/components/rich-text/rich-text.types';

import './styles/@theme';

const TYPE_PARAGRAPH = 'paragraph';
const TYPE_LIST_ITEM = 'list-item';
const TYPE_ORDER_LIST_ITEM = 'o-list-item';
const TYPE_PREFORMATTED = 'preformatted';

const generateUniqueKey = (element: RichTextElement, index: number): string => `${element.type}-${index}`;

const reduceElements = (
    res: RichTextAccumulator,
    element: RichTextElement,
    currentIndex: number,
    array: RichTextElement[]
): RichTextAccumulator => {
    if (element.type !== TYPE_LIST_ITEM && res.list.length) {
        res.out.push(<RichTextList key={`ul-${currentIndex}`} list={res.list} />);
        res.list = [];
    }
    if (element.type !== TYPE_ORDER_LIST_ITEM && res.olist.length) {
        res.out.push(<RichTextOrderList {...res.props} list={res.olist} key={`ol-${currentIndex}`} />);
        res.olist = [];
    }

    switch (element.type) {
        case TYPE_PARAGRAPH:
            res.out.push(
                <RichTextParagraph {...element} {...res.props} key={generateUniqueKey(element, currentIndex)} />
            );
            break;
        case TYPE_ORDER_LIST_ITEM:
            res.olist.push(element);
            if (currentIndex === array.length - 1) {
                res.out.push(<RichTextOrderList {...res.props} list={res.olist} key={`ol-end-${currentIndex}`} />);
                res.olist = [];
            }
            break;
        case TYPE_LIST_ITEM:
            res.list.push(element);
            if (currentIndex === array.length - 1) {
                res.out.push(<RichTextList {...res.props} list={res.list} key={`ul-end-${currentIndex}`} />);
                res.list = [];
            }
            break;
        case TYPE_HEADING.H1:
        case TYPE_HEADING.H2:
        case TYPE_HEADING.H3:
        case TYPE_HEADING.H4:
        case TYPE_HEADING.H5:
        case TYPE_HEADING.H6:
            res.out.push(
                <RichTextHeading
                    type={element.type}
                    text={element.text} // or content, etc.
                    className={element.className}
                    currencySymbol={res.props?.currencySymbol}
                    key={generateUniqueKey(element, currentIndex)}
                />
            );
            break;
        case TYPE_PREFORMATTED:
            res.out.push(
                <RichTextPreformatted {...element} {...res.props} key={generateUniqueKey(element, currentIndex)} />
            );
            break;
    }

    return res;
};

export const RichText: React.FC<RichTextProps> = (props) => {
    const { content, className, currencySymbol } = props;
    const customClasses = Array.isArray(className) ? className : className ? [className] : [];

    let richText: RichTextAccumulator = {
        out: [],
        list: [],
        olist: [],
        props: { currencySymbol }
    };

    if (Array.isArray(content)) {
        richText = content.reduce(reduceElements, richText);
    } else {
        return <div className={classes('rich-text', ...customClasses)}>{content}</div>;
    }

    return <div className={classes('rich-text', ...customClasses)}>{richText.out}</div>;
};
